import { useRef, useState, useCallback } from "react";
import { useGesture } from "@use-gesture/react";
import { motion } from "framer-motion";
import { LocationPin } from "./LocationPin.js";
import type { Location } from "../data/locations.js";

/** Illustration map image — replace with KRA's custom artwork */
/**
 * Sensoji temple illustrated map.
 * TODO: Replace with KRA's own custom illustration for production.
 */
const MAP_IMAGE = "https://www.senso-ji.jp/images_en/visit_ill01.jpg";

/** Clamp a value between min and max */
function clamp(val: number, min: number, max: number) {
  return Math.max(min, Math.min(max, val));
}

interface ImageMapProps {
  activeLocation: Location | null;
  onPinClick: (location: Location) => void;
  locations: Location[];
}

export function ImageMap({ activeLocation, onPinClick, locations }: ImageMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);

  const MIN_SCALE = 1;
  const MAX_SCALE = 3;

  useGesture(
    {
      onDrag: ({ offset: [x, y] }) => {
        setPosition({ x, y });
      },
      onPinch: ({ offset: [s] }) => {
        setScale(clamp(s, MIN_SCALE, MAX_SCALE));
      },
    },
    {
      target: containerRef,
      drag: {
        from: () => [position.x, position.y],
        bounds: () => {
          const container = containerRef.current;
          if (!container) return {};
          const w = container.offsetWidth;
          const h = container.offsetHeight;
          const boundX = Math.max(0, (w * (scale - 1)) / 2);
          const boundY = Math.max(0, (h * (scale - 1)) / 2);
          return { left: -boundX, right: boundX, top: -boundY, bottom: boundY };
        },
        rubberband: 0.15,
      },
      pinch: {
        scaleBounds: { min: MIN_SCALE, max: MAX_SCALE },
        rubberband: true,
      },
    }
  );

  const handleDoubleClick = useCallback(() => {
    if (scale > 1.2) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    } else {
      setScale(2);
    }
  }, [scale]);

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 overflow-hidden touch-none select-none"
      style={{ cursor: scale > 1 ? "grab" : "default" }}
      onDoubleClick={handleDoubleClick}
    >
      <motion.div
        className="relative w-full h-full"
        animate={{ scale, x: position.x, y: position.y }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        style={{ transformOrigin: "center center" }}
      >
        {/* Map illustration image */}
        <img
          src={MAP_IMAGE}
          alt="Asakusa Sensoji Temple illustrated map"
          className="w-full h-full object-contain"
          onLoad={() => setImageLoaded(true)}
          draggable={false}
          style={{
            opacity: imageLoaded ? 1 : 0,
            transition: "opacity 0.3s ease",
          }}
        />

        {/* Placeholder if no illustration yet */}
        {!imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-surface-1">
            <div className="text-center px-8">
              <p className="text-ink-2 text-sm mb-2">Illustrated Map</p>
              <p className="text-ink-3 text-xs">
                Tap the numbered pins to explore photo spots
              </p>
            </div>
          </div>
        )}

        {/* Pin overlays — positioned by % */}
        {locations.map((loc) => (
          <LocationPin
            key={loc.id}
            location={loc}
            isActive={activeLocation?.id === loc.id}
            onClick={() => onPinClick(loc)}
          />
        ))}
      </motion.div>
    </div>
  );
}
